/**
 * Shareable "Request a Payment" links — self-contained, no database.
 *
 * A requester composes { recipient (their OWN address), chain, token, amount, note }
 * and shares the link. When the payer opens it, the app shows a payment-details popup
 * and — on "Pay" — signs them in on that chain/token and pre-fills the transfer form.
 *
 * New format:   https://app.stabletrust.io/#r=<base64url(PaymentRequest v1)>   (fragment)
 *   The fragment is never sent to the server, so amount/note/address stay out of
 *   server & CDN access logs. This is ENCODING, not encryption — anyone with the
 *   link can decode it.
 * Legacy format: ?treq=1&treq_to&treq_chain&treq_token&treq_amount  (still parsed; no note)
 *
 * Distinct from the app's on-chain "requests" (services/requestService.js).
 */
import { ethers } from "ethers";
import { CHAINS, resolveChainKey } from "../config/chains";
import { copyTextToClipboard, canUseNativeShare } from "./inviteLink";
import { encodePaymentRequest, decodePaymentRequest } from "./paymentRequestCodec";

export const MAX_NOTE_CHARS = 100; // note cap (code points, emoji-safe)
const PARAM = "r";
const LEGACY_FLAG = "treq";
const LEGACY = { to: "treq_to", chain: "treq_chain", token: "treq_token", amount: "treq_amount" };

// ── validation / normalization helpers (shared with the builder modal) ──
export function isValidAddress(to) {
  return /^0x[0-9a-fA-F]{40}$/.test(String(to || "").trim());
}
export function normalizeAddress(to) {
  const s = String(to || "").trim();
  if (!isValidAddress(s)) throw new Error("invalid recipient address");
  try { return ethers.utils.getAddress(s); } catch { return s; } // checksum for display; ethers v5
}
export function isValidAmount(amount, decimals) {
  const a = String(amount == null ? "" : amount).trim();
  if (!/^\d+(\.\d+)?$/.test(a)) return false; // digits + optional single decimal point
  if (!/[1-9]/.test(a)) return false;         // strictly > 0
  const dot = a.indexOf(".");
  if (dot >= 0 && decimals != null && a.length - dot - 1 > decimals) return false; // within precision
  return true;
}
export function sanitizeNote(note) {
  // Strip C0 control chars (0x00-0x1F) + DEL (0x7F) so the note is single-line/URL-clean;
  // keep spaces, punctuation, emoji. Iterate by code point (emoji-safe).
  const s = String(note == null ? "" : note);
  let out = "";
  for (const ch of s) {
    const code = ch.codePointAt(0);
    if (code < 0x20 || code === 0x7f) continue;
    out += ch;
  }
  return out.trim();
}
export function noteCharCount(note) {
  return [...String(note == null ? "" : note)].length; // code points, not UTF-16 units
}

/** App origin for the link — REACT_APP_APP_ORIGIN override, else current origin. */
function appOrigin() {
  const configured = (process.env.REACT_APP_APP_ORIGIN || "").trim();
  if (configured) return configured.replace(/\/+$/, "");
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return "https://app.stabletrust.io";
}

/** Build a shareable request URL from a request descriptor. Validates all five values. */
export function buildTransferRequestUrl({ to, chainKey, tokenId, amount, note = "" }) {
  const chain = CHAINS[chainKey];
  if (!chain) throw new Error("unknown chain");
  const chainId = Number(chain.chainIdDecimal);
  if (!chainId) throw new Error("chain has no chain id");

  const tid = String(tokenId || "").toLowerCase();
  const token = chain.tokens?.[tid];
  if (!token) throw new Error("unknown token");

  const cleanTo = normalizeAddress(to);
  const cleanAmount = String(amount == null ? "" : amount).trim();
  if (!isValidAmount(cleanAmount, token.decimals)) throw new Error("invalid amount");

  const cleanNote = sanitizeNote(note);
  if (noteCharCount(cleanNote) > MAX_NOTE_CHARS) throw new Error(`note exceeds ${MAX_NOTE_CHARS} characters`);

  const blob = encodePaymentRequest({ chainId, to: cleanTo, tokenId: tid, amount: cleanAmount, note: cleanNote });
  return `${appOrigin()}/#${PARAM}=${blob}`;
}

/** Validate + normalize a raw descriptor against the chain/token registry. Returns null if invalid. */
function normalizeRequest({ chainKey, to, tokenId, amount, note }) {
  const chain = CHAINS[chainKey];
  const tid = String(tokenId || "").toLowerCase();
  const token = chain?.tokens?.[tid];
  if (!chain || !token) return null;
  if (!isValidAddress(to)) return null;
  const amt = String(amount == null ? "" : amount).trim();
  if (!isValidAmount(amt, token.decimals)) return null;
  return {
    to: normalizeAddress(to),
    chainKey,
    tokenId: tid,
    amount: amt,
    note: sanitizeNote(note).slice(0, 4096), // hard cap; char-count enforced on build
    tokenSymbol: token.symbol,
    chainName: chain.name,
    network: chain.network,
    decimals: token.decimals,
  };
}

/**
 * Parse a payment request from the current URL. Returns a validated descriptor
 * { to, chainKey, tokenId, amount, note, tokenSymbol, chainName, network, decimals }
 * or null if there is none / it is invalid or corrupted (fail-closed: the app just
 * loads normally).
 */
export function parseTransferRequestFromUrl() {
  if (typeof window === "undefined") return null;
  // New compact format in the fragment: #r=<blob>
  try {
    const hash = (window.location.hash || "").replace(/^#/, "");
    const blob = new URLSearchParams(hash).get(PARAM);
    if (blob) {
      const d = decodePaymentRequest(blob);
      const chainKey = resolveChainKey(d.chainId);
      if (!chainKey) return null;
      return normalizeRequest({ chainKey, to: d.to, tokenId: d.tokenId, amount: d.amount, note: d.note });
    }
  } catch {
    // fall through — corrupted #r= is ignored
  }
  // Legacy query format: ?treq=1&treq_*  (no note)
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get(LEGACY_FLAG) === "1" || sp.get(LEGACY.to) || sp.get(LEGACY.chain)) {
      return normalizeRequest({
        chainKey: (sp.get(LEGACY.chain) || "").trim(),
        to: (sp.get(LEGACY.to) || "").trim(),
        tokenId: (sp.get(LEGACY.token) || "").trim(),
        amount: (sp.get(LEGACY.amount) || "").trim(),
        note: "",
      });
    }
  } catch {
    // ignore
  }
  return null;
}

/** Strip the request from the visible URL (both #r= and legacy treq_*). Keeps history clean. */
export function clearTransferRequestFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    [LEGACY_FLAG, LEGACY.to, LEGACY.chain, LEGACY.token, LEGACY.amount].forEach((k) =>
      u.searchParams.delete(k),
    );
    if (u.hash) {
      const hp = new URLSearchParams(u.hash.replace(/^#/, ""));
      hp.delete(PARAM);
      const rest = hp.toString();
      u.hash = rest ? `#${rest}` : "";
    }
    window.history.replaceState({}, "", u.toString());
  } catch {}
}

/** Short message for sharing (SMS / WhatsApp / email). */
export function getTransferRequestShareText(url, { amount, tokenSymbol, note } = {}) {
  const amt = amount && tokenSymbol ? `${amount} ${tokenSymbol}` : "a confidential payment";
  const reason = note ? ` (${note})` : "";
  return (
    `You've been requested to send ${amt}${reason} on Stabletrust Pay. ` +
    `Open this link to review and pay: ${url}`
  );
}

/**
 * Open the OS share sheet with the request; falls back to copying the link.
 * Returns "shared" | "copied" | "cancelled".
 */
export async function shareTransferRequest(url, meta = {}) {
  if (canUseNativeShare()) {
    try {
      await navigator.share({
        title: "Stabletrust Pay — payment request",
        text: getTransferRequestShareText(url, meta),
      });
      return "shared";
    } catch (err) {
      if (err && err.name === "AbortError") return "cancelled";
    }
  }
  await copyTextToClipboard(url);
  return "copied";
}
